import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth as useClerkAuth, useClerk, useOrganization } from '@clerk/clerk-react';
import { User } from '../types';
import { authApi } from '../api/auth';
import client from '../api/client';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  needsOrganization: boolean;
  login: (email: string, password: string) => Promise<User>;
  loginWithClerkToken: (token: string) => Promise<User>;
  logout: () => Promise<void>;
  hasRole: (role: string | string[]) => boolean;
  markOnboardingComplete: () => void;
  resetOnboarding: () => Promise<void>;
  resyncFromClerk: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [needsOrganization, setNeedsOrganization] = useState(false);
  const { isLoaded: clerkLoaded, isSignedIn, getToken } = useClerkAuth();
  const { organization, isLoaded: orgLoaded } = useOrganization();
  const { signOut } = useClerk();
  const syncingRef = useRef(false);
  const lastOrgIdRef = useRef<string | null | undefined>(undefined);

  const loginWithClerkToken = useCallback(async (token: string) => {
    try {
      const response = await authApi.clerkSync(token);
      localStorage.setItem('julia_token', response.data.access_token);
      setUser(response.data.user);
      setNeedsOrganization(false);
      return response.data.user;
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { code?: string } } };
      if (axiosErr.response?.status === 409 && axiosErr.response.data?.code === 'organization_required') {
        localStorage.removeItem('julia_token');
        setUser(null);
        setNeedsOrganization(true);
        throw err;
      }
      throw err;
    }
  }, []);

  const resyncFromClerk = useCallback(async () => {
    if (!isSignedIn) return;
    setIsLoading(true);
    try {
      const token = await getToken();
      if (!token) return;
      await loginWithClerkToken(token);
    } catch {
      // needsOrganization géré dans loginWithClerkToken
    } finally {
      setIsLoading(false);
    }
  }, [isSignedIn, getToken, loginWithClerkToken]);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('julia_token');
    if (!token) {
      setIsLoading(false);
      return;
    }
    try {
      const response = await authApi.getMe();
      setUser(response.data.user);
      setNeedsOrganization(false);
    } catch {
      localStorage.removeItem('julia_token');
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  // Sync initiale Clerk → JULIA
  useEffect(() => {
    if (!clerkLoaded || !orgLoaded || !isSignedIn || syncingRef.current) return;
    if (localStorage.getItem('julia_token') && user && !needsOrganization) return;

    let cancelled = false;
    const sync = async () => {
      syncingRef.current = true;
      setIsLoading(true);
      try {
        const token = await getToken();
        if (!token || cancelled) return;
        await loginWithClerkToken(token);
      } catch (err: unknown) {
        const axiosErr = err as { response?: { data?: { code?: string } } };
        const orgRequired = axiosErr.response?.data?.code === 'organization_required';
        if (!cancelled && !orgRequired) {
          localStorage.removeItem('julia_token');
          setUser(null);
        }
      } finally {
        syncingRef.current = false;
        if (!cancelled) setIsLoading(false);
      }
    };
    sync();
    return () => {
      cancelled = true;
    };
  }, [clerkLoaded, orgLoaded, isSignedIn, getToken, loginWithClerkToken, user, needsOrganization]);

  // Resync quand l'organisation Clerk active change
  useEffect(() => {
    if (!clerkLoaded || !orgLoaded || !isSignedIn) return;
    const currentOrgId = organization?.id ?? null;
    if (lastOrgIdRef.current === undefined) {
      lastOrgIdRef.current = currentOrgId;
      return;
    }
    if (lastOrgIdRef.current === currentOrgId) return;
    lastOrgIdRef.current = currentOrgId;

    localStorage.removeItem('julia_token');
    setUser(null);
    void resyncFromClerk();
  }, [clerkLoaded, orgLoaded, isSignedIn, organization?.id, resyncFromClerk]);

  const login = async (email: string, password: string) => {
    const response = await authApi.login(email, password);
    localStorage.setItem('julia_token', response.data.access_token);
    setUser(response.data.user);
    setNeedsOrganization(false);
    return response.data.user;
  };

  const logout = async () => {
    localStorage.removeItem('julia_token');
    setUser(null);
    setNeedsOrganization(false);
    lastOrgIdRef.current = undefined;
    try {
      await signOut();
    } catch {
      // ignore
    }
  };

  const hasRole = (role: string | string[]) => {
    if (!user) return false;
    if (Array.isArray(role)) return role.includes(user.role);
    return user.role === role;
  };

  const markOnboardingComplete = useCallback(() => {
    setUser((current) => (current ? { ...current, onboarding_completed: true } : current));
  }, []);

  const resetOnboarding = useCallback(async () => {
    const response = await client.post<{ success: boolean; data?: { user: User } }>('/structure/onboarding/reset', {});
    const updatedUser = response.data.data?.user;
    if (updatedUser) {
      setUser(updatedUser);
    } else {
      setUser((current) => (current ? { ...current, onboarding_completed: false } : current));
    }
  }, []);

  const bootLoading = isLoading || !clerkLoaded || (isSignedIn && !orgLoaded);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading: bootLoading,
        isAuthenticated: !!user,
        needsOrganization,
        login,
        loginWithClerkToken,
        logout,
        hasRole,
        markOnboardingComplete,
        resetOnboarding,
        resyncFromClerk,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
